// src/components/Events/EventCard.jsx
import React from "react";
import {Card, Badge, Tag, Space, Avatar, Tooltip} from "antd";
import {
    CalendarOutlined,
    FileTextOutlined,
    TeamOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    FileOutlined,
} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import "./EventCard.css";

/**
 * EventCard Component
 * Displays event information in a card format
 */
const EventCard = ({event, isAdmin = false, onMarkAsRead, onMarkAsUnread}) => {
    const navigate = useNavigate();
    const canToggleRead =
        event?.can_mark_read !== undefined
            ? event.can_mark_read
            : event?.is_user_assigned !== undefined
                ? event.is_user_assigned
                : true;

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };


    const handleCardClick = () => {
        navigate(`/events/detail/${event.id}`);
    };

    const handleToggleRead = (e) => {
        if (!canToggleRead) {
            return;
        }
        e.stopPropagation();
        if (event.is_read) {
            onMarkAsUnread && onMarkAsUnread(event.id);
        } else {
            onMarkAsRead && onMarkAsRead(event.id);
        }
    };

    // Get assigned users count
    const assignedUsersCount = event.tasks?.reduce((total, task) => {
        return total + (task.assigned_users?.length || 0);
    }, 0) || event.total_assigned_users || 0;

    // Get event status badge
    const getEventStatusBadge = (status) => {
        if (!status) return null;
        const statusConfig = {
            upcoming: {color: "blue", text: "Kelgusi"},
            ongoing: {color: "orange", text: "Davom etmoqda"},
            completed: {color: "green", text: "Yakunlangan"},
            overdue: {color: "red", text: "Muddati o'tgan"},
        };
        const config = statusConfig[status] || statusConfig.upcoming;
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    return (
        <Card
            hoverable
            className={`event-card ${!event.is_read ? "event-card-unread" : ""}`}
            onClick={handleCardClick}
        >
            <div className="event-card-header">
                <div className="event-card-title">
                    <h4>{event.name}</h4>
                    <Space>
                        {!event.is_read && (
                            <Badge status="processing" text="Yangi" color="blue"/>
                        )}
                        {event.event_status && getEventStatusBadge(event.event_status)}
                    </Space>
                </div>
                {/*{canToggleRead && (*/}
                {/*  <div className="event-card-actions" onClick={(e) => e.stopPropagation()}>*/}
                {/*    <Tooltip title={event.is_read ? "O'qilmagan deb belgilash" : "O'qilgan deb belgilash"}>*/}
                {/*      {event.is_read ? (*/}
                {/*        <EyeOutlined*/}
                {/*          className="action-icon"*/}
                {/*          onClick={handleToggleRead}*/}
                {/*          style={{ color: "#52c41a" }}*/}
                {/*        />*/}
                {/*      ) : (*/}
                {/*        <EyeInvisibleOutlined*/}
                {/*          className="action-icon"*/}
                {/*          onClick={handleToggleRead}*/}
                {/*          style={{ color: "#8c8c8c" }}*/}
                {/*        />*/}
                {/*      )}*/}
                {/*    </Tooltip>*/}
                {/*  </div>*/}
                {/*)}*/}
            </div>

            <div className="event-card-info">
                <Space direction="vertical" size="small" style={{width: "100%"}}>
                    <div className="info-item">
                        <CalendarOutlined className="info-icon"/>
                        <span>{formatDate(event.datetime)}</span>
                    </div>

                    {event.tasks && event.tasks.length > 0 && (
                        <div className="info-item">
                            <FileTextOutlined className="info-icon"/>
                            <span>{event.tasks.length} ta topshiriq</span>
                        </div>
                    )}

                    {assignedUsersCount > 0 && (
                        <div className="info-item">
                            <TeamOutlined className="info-icon"/>
                            <span>{assignedUsersCount} ta ishtirokchi</span>
                        </div>
                    )}

                    {event.file && (
                        <div className="info-item">
                            <FileOutlined className="info-icon"/>
                            <a
                                href={event.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                Biriktirilgan fayl
                            </a>
                        </div>
                    )}
                </Space>
            </div>

            {isAdmin && event.total_assigned_users !== undefined && (
                <div className="event-card-stats">
                    <Tag color="green">O'qilgan: {event.read_count || 0}</Tag>
                    <Tag color="orange">O'qilmagan: {event.unread_count || 0}</Tag>
                </div>
            )}

            {event.tasks && event.tasks.length > 0 && (
                <div className="event-card-tasks">
                    <h5>Topshiriqlar:</h5>
                    {event.tasks.slice(0, 2).map((task) => (
                        <div key={task.id} className="task-preview">
                            <span className="task-title">{task.title}</span>
                            {task.assigned_users && task.assigned_users.length > 0 && (
                                <div className="task-assignees">
                                    <Avatar.Group maxCount={3} size="small">
                                        {task.assigned_users.map((user) => (
                                            <Tooltip key={user.id} title={user.name}>
                                                <Avatar src={user.picture} size="small">
                                                    {user.name?.[0]}
                                                </Avatar>
                                            </Tooltip>
                                        ))}
                                    </Avatar.Group>
                                </div>
                            )}
                        </div>
                    ))}
                    {event.tasks.length > 2 && (
                        <div className="more-tasks">+{event.tasks.length - 2} ta yana...</div>
                    )}
                </div>
            )}
        </Card>
    );
};

export default EventCard;
